import {
  clearDomiSession,
  getDomiAuthorizationHeader,
  isDomiAuthRequired,
  redirectToDomiLogin,
  resolveDomiUserTenantId,
} from '../auth/domi-auth';
import { readDevTenantId } from '../dev/defaults';
import {
  isWebOwnedRequestPathname,
  stripWebBasePath,
  withWebBasePath,
} from '../base-path';

const TENANT_HEADER = 'X-OD-Tenant';
/** Query param for EventSource URLs (browser SSE cannot send custom headers). */
const TENANT_QUERY_PARAM = 'tenant';

export function resolveTenantIdForRequest(): string | null {
  const domiTenantId = resolveDomiUserTenantId();
  if (domiTenantId) return domiTenantId;
  if (isDomiAuthRequired()) return null;
  return readDevTenantId();
}

function requestPathname(url: string): string {
  if (url.startsWith('/')) return url.split(/[?#]/u, 1)[0] || '/';
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function shouldAttachTenantAuth(url: string): boolean {
  if (!stripWebBasePath(requestPathname(url)).startsWith('/api')) return false;
  return getDomiAuthorizationHeader() != null || resolveTenantIdForRequest() != null;
}

function resolveRequestUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === 'string') {
    if (input.startsWith('/') && isWebOwnedRequestPathname(requestPathname(input))) {
      return withWebBasePath(input);
    }
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(input);
        if (url.origin === window.location.origin && isWebOwnedRequestPathname(url.pathname)) {
          url.pathname = withWebBasePath(url.pathname);
          return url.href;
        }
      } catch {
        // Non-absolute strings are already relative to the current app route.
      }
    }
    return input;
  }
  if (input instanceof URL) {
    if (input.origin === 'null' || input.protocol === 'about:') return input;
    if (typeof window === 'undefined' || input.origin !== window.location.origin) return input;
    if (!isWebOwnedRequestPathname(input.pathname)) return input;
    const prefixed = withWebBasePath(`${input.pathname}${input.search}${input.hash}`);
    return new URL(prefixed, input.origin);
  }
  if (typeof window === 'undefined') return input;
  const url = new URL(input.url);
  if (url.origin !== window.location.origin || !isWebOwnedRequestPathname(url.pathname)) return input;
  url.pathname = withWebBasePath(url.pathname);
  return new Request(url, input);
}

function withTenantAuthHeaders(
  input: RequestInfo | URL,
  init?: RequestInit,
  authorizationOverride?: string | null,
): [RequestInfo | URL, RequestInit | undefined] {
  const headers = new Headers(init?.headers);
  const authorization = authorizationOverride ?? getDomiAuthorizationHeader();
  if (authorization && !headers.has('Authorization')) {
    headers.set('Authorization', authorization);
  }
  // Informational only — daemon ignores unsigned X-OD-Tenant for identity.
  const tenantId = resolveTenantIdForRequest();
  if (tenantId && !headers.has(TENANT_HEADER)) {
    headers.set(TENANT_HEADER, tenantId);
  }
  if (!authorization && !tenantId) return [input, init];
  return [input, init ? { ...init, headers } : { headers }];
}

/**
 * Append tenant query for URLs that cannot send Authorization (iframe / img).
 * Prefer the HttpOnly signed cookie set after the first authenticated API call;
 * unsigned query is not sufficient for daemon identity.
 */
export function withTenantQueryUrl(url: string): string {
  const tenantId = resolveTenantIdForRequest();
  if (!tenantId) return url;
  if (new RegExp(`[?&]${TENANT_QUERY_PARAM}=`).test(url)) return url;
  return appendUrlQuery(url, `${TENANT_QUERY_PARAM}=${encodeURIComponent(tenantId)}`);
}

/** Append query params to a URL, using `&` when a query string already exists. */
export function appendUrlQuery(url: string, queryOrSuffix: string): string {
  const trimmed = queryOrSuffix.trim();
  if (!trimmed) return url;

  const hashIndex = trimmed.indexOf('#');
  const queryPart = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
  const hashPart = hashIndex >= 0 ? trimmed.slice(hashIndex) : '';

  const params = queryPart.replace(/^[?&]+/, '').trim();
  if (!params) return `${url}${hashPart}`;

  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${params}${hashPart}`;
}

/** @deprecated Prefer {@link withTenantQueryUrl}. */
export const withTenantSseUrl = withTenantQueryUrl;

let tenantAuthRedirectStarted = false;

async function redirectOnTenantAuthFailure(response: Response): Promise<Response> {
  if (response.status !== 401 || tenantAuthRedirectStarted || typeof window === 'undefined') {
    return response;
  }
  const payload = await response.clone().json().catch(() => null) as {
    error?: { code?: unknown };
  } | null;
  if (payload?.error?.code !== 'TENANT_AUTH_REQUIRED') return response;

  tenantAuthRedirectStarted = true;
  clearDomiSession();
  redirectToDomiLogin();
  return response;
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const resolvedInput = resolveRequestUrl(input);
  const url = typeof resolvedInput === 'string'
    ? resolvedInput
    : resolvedInput instanceof URL
      ? resolvedInput.href
      : resolvedInput.url;
  if (!shouldAttachTenantAuth(url)) {
    return redirectOnTenantAuthFailure(await fetch(resolvedInput, init));
  }
  const [nextInput, nextInit] = withTenantAuthHeaders(resolvedInput, init);
  return redirectOnTenantAuthFailure(await fetch(nextInput, nextInit));
}

let fetchPatchInstalled = false;
export function installApiFetchPatch(): void {
  if (fetchPatchInstalled || typeof window === 'undefined') return;
  fetchPatchInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const resolvedInput = resolveRequestUrl(input);
    const url = typeof resolvedInput === 'string'
      ? resolvedInput
      : resolvedInput instanceof URL
        ? resolvedInput.href
        : resolvedInput.url;
    if (!shouldAttachTenantAuth(url)) {
      return redirectOnTenantAuthFailure(await originalFetch(resolvedInput, init));
    }
    const [nextInput, nextInit] = withTenantAuthHeaders(resolvedInput, init);
    return redirectOnTenantAuthFailure(await originalFetch(nextInput, nextInit));
  };
}
